import { graphql } from '@/gql'
import { useEdgesState, useNodesState, type EdgeChange, type NodeChange } from '@xyflow/react'
import { type CapacitanceTestStepInput, type DiodeTestStepInput, type HipotTestStepInput, type ItemTypeDetailFragment, type LinkTestStepInput, type LoadTestStepInput, type NoopTestStepInput, type RcdTestStepInput, type ResistanceFourWireTestStepInput, type ResistanceTestStepInput, type SteppableInput, type TwistedPairTestStepInput, type UpsertTestStepInput, type WireFourWireTestStepInput, type WireTestStepInput } from '@/gql/graphql'
import { createContext, useCallback, useRef, useState } from 'react'
import { useMutation } from '@apollo/client/react'
import type { ValidationError } from './useForm'

export type TestStepNode = {
  id: string
  type: ItemTypeDetailFragment['testSteps'][0]['steppable']['__typename']
  data: {
    name: string
    itemTypeId: string
    connectorFamilies: ItemTypeDetailFragment['connectorFamilies']
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    steppable: { [key: string]: any }
  }
  position: {
    x: number
    y: number
  }
}

export type TestStepEdge = {
  id: string
  source: string
  target: string
}

const nodeTypeSteppableTypes: { [nodeType: string]: keyof SteppableInput } = {
  NoopTestStepType: 'noop',
  HipotTestStepType: 'hipot',
  ResistanceTestStepType: 'resistance',
  ResistanceFourWireTestStepType: 'resistanceFourWire',
  CapacitanceTestStepType: 'capacitance',
  DiodeTestStepType: 'diode',
  TwistedPairTestStepType: 'twistedPair',
  RcdTestStepType: 'rcd',
  LoadTestStepType: 'load',
  WireTestStepType: 'wire',
  WireFourWireTestStepType: 'wireFourWire',
  LinkTestStepType: 'link',
}

type StepPatch = {
  name?: string
  itemTypeId?: string
  steppable?: Omit<
  Partial<NoopTestStepInput>
  | Partial<HipotTestStepInput>
  | Partial<ResistanceTestStepInput>
  | Partial<ResistanceFourWireTestStepInput>
  | Partial<CapacitanceTestStepInput>
  | Partial<DiodeTestStepInput>
  | Partial<TwistedPairTestStepInput>
  | Partial<RcdTestStepInput>
  | Partial<LoadTestStepInput>
  | Partial<WireTestStepInput>
  | Partial<WireFourWireTestStepInput>
  | Partial<LinkTestStepInput>,
  'id' | '__typename'>
}

const UpsertTestStepMutation = graphql(`
  mutation UpsertTestStep($input: UpsertTestStepInput!) {
    upsertTestStep(input: $input) {
      testStep {
        id
        name
        itemType { id }
        positionX
        positionY
        steppable {
          __typename
          ... on NoopTestStepType { id result }
          ... on HipotTestStepType { id voltage connectorFamilyPin { id } }
          ... on ResistanceTestStepType { id connectorFamilyPinA { id } connectorFamilyPinB { id } }
          ... on ResistanceFourWireTestStepType { id connectorFamilyPinA { id } connectorFamilyPinB { id } }
          ... on CapacitanceTestStepType { id connectorFamilyPinA { id } connectorFamilyPinB { id } }
          ... on DiodeTestStepType { id connectorFamilyPinA { id } connectorFamilyPinB { id } }
          ... on TwistedPairTestStepType { id connectorFamilyPinA { id } connectorFamilyPinB { id } }
          ... on RcdTestStepType { id breakerCount phaseAngle tripCurrent }
          ... on LoadTestStepType { id loadCurrent }
          ... on WireTestStepType { id connectorFamilyPinA { id } connectorFamilyPinB { id } }
          ... on WireFourWireTestStepType { id connectorFamilyPinA { id } connectorFamilyPinB { id } }
          ... on LinkTestStepType { id connectorFamilyPinA { id } connectorFamilyPinB { id } }
        }
      }
      errors { path message }
    }
  }
`)

const DestroyTestStepMutation = graphql(`
  mutation DestroyTestStep($input: DestroyTestStepInput!) {
    destroyTestStep(input: $input) {
      testStep { id }
      errors { path message }
    }
  }
`)

const CreateTestStepDependencyMutation = graphql(`
  mutation CreateTestStepDependency($input: CreateTestStepDependencyInput!) {
    createTestStepDependency(input: $input) {
      testStepDependency { id testStep { id } dependency { id } }
      errors { path message }
    }
  }
`)

const DestroyTestStepDependencyMutation = graphql(`
  mutation DestroyTestStepDependency($input: DestroyTestStepDependencyInput!) {
    destroyTestStepDependency(input: $input) {
      testStepDependency { id }
      errors { path message }
    }
  }
`)

export const StepActionsContext = createContext<{ changeStep: (id: string, p: StepPatch) => void }>(null!)

export const useTestDefinition = (initialNodes: TestStepNode[], initialEdges: TestStepEdge[]) => {
  const [nodes, setNodes, baseOnNodesChange] = useNodesState<TestStepNode>(initialNodes)
  const [edges, setEdges, baseOnEdgesChange] = useEdgesState(initialEdges)

  const [fieldErrors, setFieldErrors] = useState<{ [field: string]: string }>({})
  const [mutationError, setMutationError] = useState<string>()
  const [saveSuccess, setSaveSuccess] = useState<boolean>()

  const saveTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>)
  const savePending = useRef(new Map<string, UpsertTestStepInput>)

  const [upsertTestStep, { loading: upsertLoading }] = useMutation(UpsertTestStepMutation)
  const [destroyTestStep, { loading: destroyLoading }] = useMutation(DestroyTestStepMutation)
  const [createTestStepDependency, { loading: createDepLoading }] = useMutation(CreateTestStepDependencyMutation)
  const [destroyTestStepDependency, { loading: destroyDepLoading }] = useMutation(DestroyTestStepDependencyMutation)

  const onNodesChange = (changes: NodeChange<TestStepNode>[]) => {
    baseOnNodesChange(changes)
    changes.forEach(change => {
      switch (change.type) {
        case 'position': {
          changeStep(change.id, {})
          break
        }
        case 'remove': {
          deleteStep(change.id)
          break
        }
        default:
      }
    })
  }

  const onEdgesChange = (changes: EdgeChange<TestStepEdge>[]) => {
    baseOnEdgesChange(changes)
    changes.forEach(change => {
      switch (change.type) {
        case 'add': {
          addEdge(change.item.source, change.item.target)
          break
        }
        case 'remove': {
          deleteEdge(change.id)
          break
        }
        default:
      }
    })
  }

  const saveStep = useCallback((id: string) => {
    const patch = savePending.current.get(id)
    savePending.current.delete(id)
    if (patch) {
      upsertTestStep({ variables: { input: { id: id.startsWith('dndnode') ? undefined : id, ...patch } } })
        .then(response => {
          const error = response.error?.message
          const validationErrors = {} as Record<string, string>
          (response.data?.upsertTestStep?.errors as Array<ValidationError> || [])
            .forEach((e: ValidationError) => validationErrors[e.path] = e.message)

          if (!error && Object.keys(validationErrors).length == 0) {
            setSaveSuccess(true)
          } else {
            setSaveSuccess(false)
          }

          setTimeout(() => {
            setSaveSuccess(undefined)
          }, 1500)

          setFieldErrors(validationErrors)
          setMutationError(error)

          setNodes(nodes => nodes.map(node => node.id === id ? { ...node, id: response.data?.upsertTestStep?.testStep?.id || id } : node))
        })
        .catch(e => setMutationError(e.errors?.[0]?.message || 'Something went wrong when saving'))
    }
  }, [setNodes, upsertTestStep])

  const destroyStep = useCallback((id: string) => {
    destroyTestStep({ variables: { input: { id } } })
      .then(response => {
        const error = response.error?.message
        const validationErrors = {} as Record<string, string>
        (response.data?.destroyTestStep?.errors as Array<ValidationError> || [])
          .forEach((e: ValidationError) => validationErrors[e.path] = e.message)

        if (!error && Object.keys(validationErrors).length == 0) {
          setSaveSuccess(true)
        } else {
          setSaveSuccess(false)
        }

        setTimeout(() => {
          setSaveSuccess(undefined)
        }, 1500)

        setFieldErrors(validationErrors)
        setMutationError(error)
      })
      .catch(e => setMutationError(e.errors?.[0]?.message || 'Something went wrong when saving'))
  }, [destroyTestStep])

  const saveDependency = useCallback((testStepId: string, dependencyId: string) => {
    return createTestStepDependency({ variables: { input: { testStepId, dependencyId } } })
      .then(response => {
        const error = response.error?.message
        const validationErrors = {} as Record<string, string>
        (response.data?.createTestStepDependency?.errors as Array<ValidationError> || [])
          .forEach((e: ValidationError) => validationErrors[e.path] = e.message)

        if (!error && Object.keys(validationErrors).length == 0) {
          setSaveSuccess(true)
        } else {
          setSaveSuccess(false)
        }

        setTimeout(() => {
          setSaveSuccess(undefined)
        }, 1500)

        setFieldErrors(validationErrors)
        setMutationError(error)

        return response.data?.createTestStepDependency?.testStepDependency?.id
      })
      .catch(e => setMutationError(e.errors?.[0]?.message || 'Something went wrong when saving'))
  }, [createTestStepDependency])

  const destroyDependency = useCallback((id: string) => {
    destroyTestStepDependency({ variables: { input: { id } } })
      .then(response => {
        const error = response.error?.message
        const validationErrors = {} as Record<string, string>
        (response.data?.destroyTestStepDependency?.errors as Array<ValidationError> || [])
          .forEach((e: ValidationError) => validationErrors[e.path] = e.message)

        if (!error && Object.keys(validationErrors).length == 0) {
          setSaveSuccess(true)
        } else {
          setSaveSuccess(false)
        }

        setTimeout(() => {
          setSaveSuccess(undefined)
        }, 1500)

        setFieldErrors(validationErrors)
        setMutationError(error)
      })
      .catch(e => setMutationError(e.errors?.[0]?.message || 'Something went wrong when saving'))
  }, [destroyTestStepDependency])

  const changeStep = useCallback((id: string, patch: StepPatch) => {
    setNodes(nodes => nodes.map(node => {
      if (node.id === id) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { __typename: _, ...steppableFields } = { __typename: null, ...node.data.steppable, ...patch.steppable }

        savePending.current.set(id, {
          ...savePending.current.get(id),
          ...node.data.itemTypeId !== undefined && node.data.itemTypeId !== null && { itemTypeId: node.data.itemTypeId },
          ...node.data.name !== undefined && node.data.name !== null && { name: node.data.name },
          ...patch,
          positionX: node.position.x,
          positionY: node.position.y,
          steppable: { [nodeTypeSteppableTypes[node.type]]: steppableFields } as unknown as SteppableInput,
        })

        return {
          ...node,
          data: {
            ...node.data,
            ...patch.name !== undefined && patch.name !== null && { name: patch.name },
            steppable: { ...node.data.steppable, ...patch.steppable },
          },
        }
      } else {
        return node
      }
    }))

    clearTimeout(saveTimers.current.get(id))
    saveTimers.current.set(id, setTimeout(() => saveStep(id), 500))
  }, [setNodes, saveStep])

  const deleteStep = useCallback((id: string) => {
    setNodes(nodes => nodes.map(node => node.id === id ? undefined : node).filter(n => n !== undefined))
    destroyStep(id)
  }, [setNodes, destroyStep])

  const addEdge = useCallback((source: string, target: string) => {
    saveDependency(source, target)
      .then(id => {
        if (!id) return
        setEdges(edges => [...edges, { id, source, target }])
      })
  }, [setEdges, saveDependency])

  const deleteEdge = useCallback((id: string) => {
    setEdges(edges => edges.map(edge => edge.id === id ? undefined : edge).filter(e => e !== undefined))
    destroyDependency(id)
  }, [setEdges, destroyDependency])

  return { nodes, edges, loading: upsertLoading || destroyLoading || createDepLoading || destroyDepLoading, saveSuccess, mutationError, fieldErrors, onNodesChange, onEdgesChange, changeStep, deleteStep, addEdge, deleteEdge }
}
